// Thin wrapper around the canonical notificationSound module so the
// premium-named API used by NotificationBell keeps working.
import { playNotificationSound } from "@/lib/notificationSound";

let audioContext: AudioContext | null = null;
const STORAGE_KEY = "berastays:notification-sound-enabled";

export type PremiumSoundType =
  | "message"
  | "booking"
  | "payment"
  | "offer"
  | "alert"
  | "default";

export function preloadAudioContext(): void {
  try {
    if (!audioContext && typeof window !== "undefined") {
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
  } catch {
    /* ignore */
  }
}

export function isSoundEnabled(): boolean {
  if (typeof window === "undefined") return true;
  const v = localStorage.getItem(STORAGE_KEY);
  return v === null ? true : v === "true";
}

export function setSoundEnabled(enabled: boolean): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEY, String(enabled));
}

export function getSoundTypeForNotification(type?: string | null): PremiumSoundType {
  switch (type) {
    case "new_message":
      return "message";
    case "booking_created":
    case "booking_confirmed":
    case "affiliate_booking_created":
    case "affiliate_booking_confirmed":
      return "booking";
    case "payment_received":
    case "host_balance_converted":
    case "affiliate_balance_update":
      return "payment";
    case "offer_received":
    case "offer_accepted":
    case "counter_offer_received":
      return "offer";
    default:
      return "default";
  }
}

export function playPremiumSound(_type: PremiumSoundType = "default", _force?: boolean): void {
  if (!_force && !isSoundEnabled()) return;
  
  try {
    if (!audioContext && typeof window !== "undefined") {
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (!audioContext) return;

    if (audioContext.state === "suspended") {
      audioContext.resume().catch(() => {});
    }

    const now = audioContext.currentTime;
    
    // Iconic iPhone "Tri-tone" frequencies: D5, A5, F#5
    const notes = [
      { freq: 587.33, start: 0, duration: 0.15 },
      { freq: 880.00, start: 0.15, duration: 0.15 },
      { freq: 739.99, start: 0.30, duration: 0.4 }
    ];

    notes.forEach(note => {
      const osc = audioContext!.createOscillator();
      const gain = audioContext!.createGain();

      // Sine wave for that clean, premium Apple-like bell sound
      osc.type = "sine";
      osc.frequency.setValueAtTime(note.freq, now + note.start);

      // Fast attack, exponential decay for a bell-like envelope
      gain.gain.setValueAtTime(0, now + note.start);
      gain.gain.linearRampToValueAtTime(0.4, now + note.start + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.001, now + note.start + note.duration);

      osc.connect(gain);
      gain.connect(audioContext!.destination);

      osc.start(now + note.start);
      osc.stop(now + note.start + note.duration);
    });
  } catch {
    // Fallback to classic if Web Audio fails
    playNotificationSound();
  }
}
